import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationBatchManager } from '../../../services/TranslationService';
import { useOrder } from '../../../contexts/OrderContext';
import ReactMarkdown from 'react-markdown';
import { createPortal } from 'react-dom';

// The model writes the suggestion `reason` with "the bolded driver" up front and
// is inconsistent about HOW it bolds: sometimes markdown (**Attached
// electronics.**), sometimes raw HTML (<b>Attached electronics.</b>). The
// tooltip renders markdown, so the HTML shape leaked through as literal tags.
// Normalize the small inline set to markdown and drop every other tag — the
// output is then plain markdown, so no raw HTML is ever rendered.
export function toInlineMarkdown(text: string): string {
  return String(text ?? '')
    .replace(/<\s*(?:b|strong)\s*>([\s\S]*?)<\s*\/\s*(?:b|strong)\s*>/gi, '**$1**')
    .replace(/<\s*(?:i|em)\s*>([\s\S]*?)<\s*\/\s*(?:i|em)\s*>/gi, '_$1_')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/?[a-z][^>]*>/gi, '')   // any surviving tag: strip, never render
    .trim();
}

interface ComplementPartSuggestion {
  suggested_part_name: string;
  linked_to: string;
  probability: 'Very High' | 'High' | 'Medium' | 'Low';
  reason: string;
  suggested_part_id: string;
}

interface QuerySearchViewProps {
  suggestions: ComplementPartSuggestion[];
  /** Parts from the diagram — used to detect when a complementary part can be added directly to cart */
  imageParts?: Array<any> | null;
  /** Called to add a part directly to cart when it exists in image_parts */
  onAddToCart?: (part: any, structuredData: any) => Promise<void> | void;
  /** Set of part identifiers already in the cart (lowercase) */
  cartPartIdentifiers?: Set<string>;
  /** Structured data to pass along when adding to cart */
  structuredData?: any;
  /** Raw diagram image data (base64 or URL) used to generate a highlighted snapshot */
  diagramImage?: string | null;
  /** Callout data for the diagram — used to highlight the matching callout on the image */
  imageCallouts?: Array<any> | null;
  /** Function that draws a highlight rect / pill over the target callout and returns a data-URL */
  createHighlightedImage?: (imageData: string, callouts: any[], calloutLabel: string) => Promise<string>;
  /** How many suggestions to render (default 5). The aggregated whole-order view
      passes a higher cap — it merges every requested part's suggestions into one
      section, so 5 would hide most of them. */
  maxVisible?: number;
  /** Opens the concrete catalog part on its diagram/callout map. */
  onShowOnMap?: (suggestion: ComplementPartSuggestion) => void;
}

const PROBABILITY_STYLES: Record<string, { bg: string; color: string }> = {
  'Very High': { bg: '#d1fae5', color: '#065f46' },
  'High':      { bg: '#dbeafe', color: '#1e40af' },
  'Medium':    { bg: '#fef9c3', color: '#854d0e' },
  'Low':       { bg: '#f3f4f6', color: '#6b7280' },
};

const PROBABILITY_ORDER: Record<string, number> = {
  'Very High': 0,
  'High': 1,
  'Medium': 2,
  'Low': 3,
};

// ── Reserved slot ────────────────────────────────────────────────────────────
// Complementary suggestions are produced by a deferred round AFTER the turn's
// results ship, so they land ~2.5s late via a history reload. Mounting them
// into a zero-height gap made the message grow under the reader — the "screen
// jump". This placeholder holds the slot open from the moment the results
// render, so the real suggestions cross-fade into space that is already there.
//
// Height fidelity matters more than looks here: every wrapper below mirrors a
// real suggestion row's box exactly (same border, radius, padding, font sizes,
// gaps), and each shimmer bar is absolutely positioned over a non-breaking
// space so the line box is a REAL text line box of the same height. Change a
// row's chrome above and you must change it here too.
const SkeletonBar: React.FC<{ width: string; inset?: string }> = ({ width, inset = '0.18em 0 0.18em 0' }) => (
  <span style={{ position: 'relative', display: 'block', width }}>
    &nbsp;
    <span
      aria-hidden
      className="animate-pulse"
      style={{ position: 'absolute', inset, backgroundColor: '#ecebe7', borderRadius: 4 }}
    />
  </span>
);

export const ComplementarySkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-2" aria-busy="true" aria-live="polite">
      <p className="text-xs font-medium mb-2" style={{ color: '#c9c7c2' }}>
        {t('chat.querySearch.complementaryParts')}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {Array.from({ length: Math.max(1, rows) }).map((_, i) => (
          <div
            key={i}
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #f0efec',
              borderRadius: '8px',
              padding: '5px 9px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, flex: 1, minWidth: 0 }}>
                <SkeletonBar width={['70%', '58%', '76%', '64%'][i % 4]} />
              </span>
              <span style={{ fontSize: '9px', fontWeight: 600, padding: '1px 6px', flexShrink: 0 }}>
                <SkeletonBar width="26px" />
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// The tooltip renders through a PORTAL with fixed positioning, not as an
// absolutely-positioned child. Its ancestors (the results table wrapper, the
// message bubble) set `overflow: hidden`, which clipped the bubble the moment a
// row sat near an edge — the user saw a tooltip sliced in half. Fixed + portal
// escapes every clipping context; the position is measured from the icon on
// open and clamped into the viewport, and it hides on scroll/resize rather than
// drifting away from its anchor.
const TOOLTIP_WIDTH = 240;
const TOOLTIP_MARGIN = 8;

const InfoTooltip: React.FC<{ reason: string; linkedLabel?: string }> = ({ reason, linkedLabel }) => {
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const place = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Centre on the icon, then clamp so the bubble never leaves the viewport.
    const half = TOOLTIP_WIDTH / 2;
    const left = Math.min(
      Math.max(r.left + r.width / 2 - half, TOOLTIP_MARGIN),
      Math.max(TOOLTIP_MARGIN, window.innerWidth - TOOLTIP_WIDTH - TOOLTIP_MARGIN),
    );
    // Prefer above; flip below when the top of the screen is too close.
    const below = r.top < 140;
    setPos({ left, top: below ? r.bottom + 6 : r.top - 6, below });
  }, []);

  useEffect(() => {
    if (!pos) return;
    const hide = () => setPos(null);
    // Capture phase: the chat scroller is a nested element, not window.
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [pos]);

  return (
    <span
      ref={anchorRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '4px', flexShrink: 0 }}
      onMouseEnter={place}
      onMouseLeave={() => setPos(null)}
    >
      <svg
        width="13" height="13" viewBox="0 0 20 20" fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ cursor: 'pointer', color: '#9ca3af' }}
      >
        <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.8" />
        <text x="10" y="15" textAnchor="middle" fontSize="12" fontWeight="700" fill="currentColor">i</text>
      </svg>
      {pos && createPortal(
        <span style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          transform: pos.below ? 'none' : 'translateY(-100%)',
          backgroundColor: '#1f2937',
          color: '#f9fafb',
          fontSize: '11px',
          lineHeight: '15px',
          padding: '7px 10px',
          borderRadius: '7px',
          whiteSpace: 'normal',
          width: TOOLTIP_WIDTH,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          pointerEvents: 'none',
        }}>
          {linkedLabel && (
            <span style={{ display: 'block', color: '#c7c5f5', fontWeight: 600, marginBottom: 3 }}>
              {linkedLabel}
            </span>
          )}
          <ReactMarkdown
            components={{
              // The tooltip is a narrow inline bubble — every block element
              // collapses to a plain span so a stray paragraph/heading/list from
              // the model can't add margins or bullets inside it.
              p: ({ children }) => <span>{children}</span>,
              h1: ({ children }) => <span style={{ fontWeight: 700 }}>{children}</span>,
              h2: ({ children }) => <span style={{ fontWeight: 700 }}>{children}</span>,
              h3: ({ children }) => <span style={{ fontWeight: 700 }}>{children}</span>,
              ul: ({ children }) => <span>{children}</span>,
              ol: ({ children }) => <span>{children}</span>,
              li: ({ children }) => <span>{children} </span>,
              strong: ({ children }) => <strong style={{ fontWeight: 700, color: '#ffffff' }}>{children}</strong>,
              em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
              code: ({ children }) => <span style={{ fontFamily: 'monospace' }}>{children}</span>,
              // No navigation out of a tooltip the user can't even click.
              a: ({ children }) => <span>{children}</span>,
            }}
          >
            {toInlineMarkdown(reason)}
          </ReactMarkdown>
        </span>,
        document.body,
      )}
    </span>
  );
};

const QuerySearchView: React.FC<QuerySearchViewProps> = ({
  suggestions,
  imageParts,
  onAddToCart,
  cartPartIdentifiers,
  structuredData,
  diagramImage,
  imageCallouts,
  createHighlightedImage,
  maxVisible = 5,
  onShowOnMap,
}) => {
  const { t, i18n } = useTranslation();
  const { getActiveOrderId } = useOrder();
  const [addingIds, setAddingIds] = React.useState<Set<string>>(new Set());
  const [translatedNames, setTranslatedNames] = useState<Record<string, string>>({});
  const [translatedLinkedTo, setTranslatedLinkedTo] = useState<Record<string, string>>({});
  const translationCacheRef = useRef<Map<string, { names: Record<string, string>; linkedTo: Record<string, string> }>>(new Map());

  // Build a stable key based on suggestions content + image part descriptions + language
  const translationKey = useMemo(() => {
    if (!suggestions || suggestions.length === 0) return '';
    const content = suggestions.map((s, idx) => {
      const matchedPart = imageParts && s.suggested_part_id
        ? imageParts.find((p: any) =>
            String(p.partId) === String(s.suggested_part_id) ||
            String(p.crossCatKey) === String(s.suggested_part_id) ||
            String(p.part_id) === String(s.suggested_part_id)
          )
        : null;
      const partDesc = matchedPart?.description || matchedPart?.name || '';
      return `${s.suggested_part_name}|${s.linked_to}|${partDesc}`;
    }).join('::');
    return `${content}::${i18n.language}`;
  }, [suggestions, imageParts, i18n.language]);

  useEffect(() => {
    setTranslatedNames({});
    setTranslatedLinkedTo({});

    if (!suggestions || suggestions.length === 0 || !translationKey) return;

    const cached = translationCacheRef.current.get(translationKey);
    if (cached) {
      setTranslatedNames(cached.names);
      setTranslatedLinkedTo(cached.linkedTo);
      return;
    }

    let cancelled = false;
    const currentLang = i18n.language;

    const runTranslations = async () => {
      const items: Array<{ key: string; text: string }> = [];
      suggestions.forEach((s, idx) => {
        if (s.suggested_part_name) items.push({ key: `name_${idx}`, text: s.suggested_part_name });
        if (s.linked_to) items.push({ key: `linked_${idx}`, text: s.linked_to });
        // Also translate the matched image part's description (used as cart item description)
        const matchedPart = imageParts && s.suggested_part_id
          ? imageParts.find((p: any) =>
              String(p.partId) === String(s.suggested_part_id) ||
              String(p.crossCatKey) === String(s.suggested_part_id) ||
              String(p.part_id) === String(s.suggested_part_id)
            )
          : null;
        const partDesc = matchedPart?.description || matchedPart?.name || '';
        if (partDesc && partDesc !== s.suggested_part_name) {
          items.push({ key: `part_desc_${idx}`, text: partDesc });
        }
      });

      if (items.length === 0) return;

      const batchTranslations = await TranslationBatchManager.request(
        String(getActiveOrderId() ?? ''),
        currentLang,
        items,
      );
      if (cancelled) return;

      const names: Record<string, string> = {};
      const linkedTo: Record<string, string> = {};
      suggestions.forEach((s, idx) => {
        const n = batchTranslations[`name_${idx}`];
        const l = batchTranslations[`linked_${idx}`];
        const pd = batchTranslations[`part_desc_${idx}`];
        if (n) names[s.suggested_part_name] = n;
        if (l) linkedTo[s.linked_to] = l;
        // Store part description translation under its original text key
        const matchedPart = imageParts && s.suggested_part_id
          ? imageParts.find((p: any) =>
              String(p.partId) === String(s.suggested_part_id) ||
              String(p.crossCatKey) === String(s.suggested_part_id) ||
              String(p.part_id) === String(s.suggested_part_id)
            )
          : null;
        const partDesc = matchedPart?.description || matchedPart?.name || '';
        if (pd && partDesc) names[partDesc] = pd;
      });

      translationCacheRef.current.set(translationKey, { names, linkedTo });
      setTranslatedNames(names);
      setTranslatedLinkedTo(linkedTo);
    };

    runTranslations();
    return () => { cancelled = true; };
  }, [translationKey]);

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  // "You might also need" is an available-parts list. Do not render a model
  // suggestion unless this response contains the concrete catalog part needed
  // to act on it; historically those rows fell through to onSearchPart and
  // started a second research turn when clicked.
  const availableSuggestions = suggestions.filter((suggestion) =>
    Boolean(suggestion.suggested_part_id) && Boolean(imageParts?.some((p: any) =>
      String(p.partId) === String(suggestion.suggested_part_id) ||
      String(p.partNumber) === String(suggestion.suggested_part_id) ||
      String(p.part_number) === String(suggestion.suggested_part_id) ||
      String(p.crossCatKey) === String(suggestion.suggested_part_id) ||
      String(p.part_id) === String(suggestion.suggested_part_id)
    ))
  );

  if (availableSuggestions.length === 0) return null;

  const sorted = [...availableSuggestions].sort(
    (a, b) => (PROBABILITY_ORDER[a.probability] ?? 99) - (PROBABILITY_ORDER[b.probability] ?? 99)
  );

  return (
    // Fade ONLY — no slide. The block lands in a slot the skeleton already
    // reserved, so translating it would reintroduce the movement we removed.
    <div className="mt-2 animate-in fade-in duration-500">
      <p className="text-xs font-medium mb-2" style={{ color: '#999' }}>
        {t('chat.querySearch.complementaryParts')}
      </p>
      {/* Two columns from `sm` up: 8 suggestions become 4 short rows instead of
          8 tall ones, so a fuller list costs far less vertical space. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {sorted.slice(0, maxVisible).map((suggestion, index) => {
          const probStyle = PROBABILITY_STYLES[suggestion.probability] ?? PROBABILITY_STYLES['Low'];

          // Find the matching image_part by partId == suggested_part_id
          const matchedImagePart = imageParts && suggestion.suggested_part_id
            ? imageParts.find((p: any) =>
                String(p.partId) === String(suggestion.suggested_part_id) ||
                String(p.crossCatKey) === String(suggestion.suggested_part_id) ||
                String(p.part_id) === String(suggestion.suggested_part_id)
              )
            : null;

          const canAddToCart = !!(matchedImagePart && onAddToCart);
          const partIdentifier = (matchedImagePart?.partNumber || matchedImagePart?.part_number || matchedImagePart?.description || '').toLowerCase();
          const partIdKey = (matchedImagePart?.crossCatKey || matchedImagePart?.partId || '').toString().toLowerCase();
          const isInCart = canAddToCart && cartPartIdentifiers
            ? (cartPartIdentifiers.has(partIdentifier) || cartPartIdentifiers.has(partIdKey))
            : false;
          const suggestionKey = suggestion.suggested_part_id || String(index);
          const isAdding = addingIds.has(suggestionKey);
          // The "Linked to: X" line no longer has its own row (see below); keep the
          // information reachable as the row's tooltip so nothing is actually lost.
          const displayName = translatedNames[suggestion.suggested_part_name] || suggestion.suggested_part_name;
          const partNumber = String(
            matchedImagePart?.partNumber || matchedImagePart?.part_number || ''
          ).trim();
          // "Linked to: X" lost its own row (see below) and now heads the info
          // tooltip. It is NOT a native `title` on the row: that opened a second,
          // competing browser tooltip right next to the rich one.
          const linkedLabel = suggestion.linked_to
            ? `${t('chat.querySearch.linkedTo')} ${translatedLinkedTo[suggestion.linked_to] || suggestion.linked_to}`
            : undefined;
          const cartActionLabel = isInCart
            ? t('chat.multiPart.inCart', { defaultValue: 'In cart' })
            : isAdding
              ? t('chat.multiPart.adding', { defaultValue: 'Adding...' })
              : t('chat.multiPart.addToCart', { defaultValue: 'Add to cart' });

          const handleClick = async () => {
            if (canAddToCart && !isInCart && !isAdding) {
              setAddingIds(prev => new Set(prev).add(suggestionKey));
              try {
                // Resolve the callout label for this part (same identifiers used in ImageWithCallouts)
                const calloutLabel: string =
                  matchedImagePart.crossCatKey ||
                  matchedImagePart.calloutLabel ||
                  String(matchedImagePart.callout ?? '') ||
                  String(matchedImagePart.index ?? '') ||
                  matchedImagePart.part_callout_id ||
                  '';

                // Build a highlighted image if all the required data is available
                let imageToUse: string | null = matchedImagePart.image || null;
                if (
                  diagramImage &&
                  imageCallouts && Array.isArray(imageCallouts) &&
                  calloutLabel &&
                  createHighlightedImage
                ) {
                  const matchingCallout = imageCallouts.find((c: any) => c.label === calloutLabel);
                  if (matchingCallout) {
                    imageToUse = await createHighlightedImage(diagramImage, imageCallouts, calloutLabel);
                  }
                }

                await onAddToCart!(
                  {
                    partNumber: matchedImagePart.partNumber || matchedImagePart.part_number || '',
                    // OE part number FIRST — crossCatKey is the diagram callout,
                    // shared by left/right pairs, so keying cart identity on it
                    // merges the second side into the first line's quantity.
                    partId: matchedImagePart.partNumber || matchedImagePart.part_number || matchedImagePart.partId || matchedImagePart.part_id || matchedImagePart.crossCatKey || '',
                    description: matchedImagePart.description || matchedImagePart.name || suggestion.suggested_part_name,
                    price: matchedImagePart.pricing_data || null,
                    image: imageToUse,
                    translated_description: translatedNames[matchedImagePart.description || matchedImagePart.name || suggestion.suggested_part_name] || translatedNames[suggestion.suggested_part_name] || null,
                  },
                  structuredData ?? null
                );
              } finally {
                setAddingIds(prev => { const next = new Set(prev); next.delete(suggestionKey); return next; });
              }
            }
          };

          return (
            <div
              key={suggestionKey}
              onClick={() => onShowOnMap?.(suggestion)}
              role={onShowOnMap ? 'button' : undefined}
              tabIndex={onShowOnMap ? 0 : undefined}
              onKeyDown={(e) => {
                if (onShowOnMap && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onShowOnMap(suggestion);
                }
              }}
              className="group text-left transition-all duration-200"
              style={{
                backgroundColor: '#ffffff',
                border: `1px solid ${isInCart ? '#a7f3d0' : '#e5e3df'}`,
                borderRadius: '8px',
                padding: '5px 9px',
                cursor: onShowOnMap ? 'pointer' : 'default',
                opacity: isAdding ? 0.7 : 1,
                minWidth: 0,
              }}
              onMouseEnter={e => { if (!isInCart && !isAdding) e.currentTarget.style.borderColor = '#6866ff'; }}
              onMouseLeave={e => { if (!isInCart && !isAdding) e.currentTarget.style.borderColor = '#e5e3df'; }}
            >
              {/* ONE compact line: name · probability · why · action.
                  This block sat under every results table at up to 8 suggestions
                  x ~59px (two stacked rows each) and ate most of a screen. The
                  "Linked to: X" line was the least load-bearing part of it —
                  especially now the set is scoped to the whole job rather than
                  per part — so it moved into the tooltip and the title attribute,
                  which halves the row. The grid below does the rest. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                <span
                  style={{
                    flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '5px',
                  }}
                >
                  <span
                    title={displayName}
                    style={{
                      fontSize: '12px', fontWeight: 600, color: '#201e1a', minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {displayName}
                  </span>
                  {partNumber && (
                    <span
                      title={`${t('chat.multiPart.partNumber', { defaultValue: 'Part #' })} ${partNumber}`}
                      style={{ fontSize: '10px', fontWeight: 500, color: '#77736c', flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      · {t('chat.multiPart.partNumber', { defaultValue: 'Part #' })} {partNumber}
                    </span>
                  )}
                </span>
                <span style={{
                  fontSize: '9px', fontWeight: 600, padding: '1px 6px', borderRadius: '20px',
                  backgroundColor: probStyle.bg, color: probStyle.color, flexShrink: 0,
                }}>
                  {t(`chat.querySearch.probability.${suggestion.probability}`, { defaultValue: suggestion.probability })}
                </span>
                {(suggestion.reason || linkedLabel) && (
                  <InfoTooltip reason={suggestion.reason || ''} linkedLabel={linkedLabel} />
                )}
                {/* Cart status / action — icon only; the label lived on its own
                    line before and is what made the row two-high. */}
                {onShowOnMap && (
                  <span title={t('chat.multiPart.showOnMap', { defaultValue: 'Show on map' })} style={{ flexShrink: 0, display: 'inline-flex', color: '#6866ff' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3z" />
                      <path d="M9 3v15M15 6v15" />
                    </svg>
                  </span>
                )}
                {canAddToCart && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); void handleClick(); }}
                    disabled={isAdding || isInCart}
                    style={{ flexShrink: 0, display: 'inline-flex', padding: 0, border: 0, background: 'transparent', cursor: isInCart ? 'default' : isAdding ? 'wait' : 'pointer' }}
                    aria-label={cartActionLabel}
                    title={cartActionLabel}
                  >
                    {isInCart ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 20 20" fill="#137f67">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : isAdding ? (
                      <svg className="animate-spin" style={{ width: '13px', height: '13px' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="#6866ff" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="#6866ff" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 20 20" fill="#6866ff">
                        <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuerySearchView;
